import { Link } from 'react-router-dom';
import { daysLeftLabel, formatUtcDate } from './formatDate';
import type { CompanySubscriptionResponse } from './types';

/**
 * EPN-79 / EPN-105 — the subscription/trial state layered onto the existing Admin Dashboard (approved Trial Dashboard
 * State SRS §2: trial elements are layered in, never a separate dashboard architecture). Every state is
 * rendered from the SERVER's `effectiveState` — nothing here decides expiry or grace from the browser clock.
 *
 *  - loading → skeleton (never briefly shows a paid or expired state before the real one is known, §53)
 *  - error → "couldn't verify", no guess (§54)
 *  - no subscription / trial not started / trial active / trial expired / active paid / plan change pending /
 *    payment failed (access continuing, or read-only) / canceled
 *
 * Deliberately NOT shown: a setup-progress or payroll-readiness figure. The company-level setup readiness
 * source (EPRFW-87) does not exist on this codebase yet (BACKEND GAP — employee-setup readiness is
 * per-employee only), and the SRS forbids inferring readiness from page visits (§47).
 */
export function TrialStatusCard({
  subscription, loading, error, onRetry,
}: {
  subscription: CompanySubscriptionResponse | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  if (loading) {
    return (
      <div className="trial-card trial-card--loading" aria-busy="true" aria-label="Loading subscription status" data-testid="trial-status-loading">
        <div className="ed-skeleton" style={{ height: 56, borderRadius: 10 }} />
      </div>
    );
  }

  if (error) {
    return (
      <section className="trial-card trial-card--attention" role="alert" data-testid="trial-status-error">
        <h3 className="trial-card__title">We couldn&apos;t verify your subscription status.</h3>
        <p>Some subscription-related actions may be temporarily unavailable.</p>
        <div className="trial-card__actions"><button type="button" onClick={onRetry}>Retry</button></div>
      </section>
    );
  }

  if (!subscription) {
    return (
      <section className="trial-card" aria-label="Subscription status" data-testid="trial-status-none">
        <h3 className="trial-card__title">No plan selected yet</h3>
        <p>Start a free 30-day trial (no credit card needed) or subscribe. You can set up your company now; payroll processing unlocks once you start.</p>
        <div className="trial-card__actions">
          <Link className="trial-card__cta" to="/settings/subscription">Start free trial</Link>
          <Link to="/settings/subscription">View Plans</Link>
        </div>
      </section>
    );
  }

  switch (subscription.effectiveState) {
    case 'TrialActive':
      return (
        <section className="trial-card trial-card--active" aria-label="Free trial status" data-testid="trial-status-active">
          <h3 className="trial-card__title">
            Trial Active{daysLeftLabel(subscription.trialDaysRemaining) ? ` — ${daysLeftLabel(subscription.trialDaysRemaining)}` : ''}
          </h3>
          <p>
            You&apos;re using Escochex Payroll on a free trial. <strong>Trial Plan: {subscription.planName}</strong> ·
            ends {formatUtcDate(subscription.trialEndUtc)}. No card is on file, and we will never charge you automatically.
          </p>
          <p className="muted">
            Live payroll, Direct Deposit, contractor payments, and tax payments require account activation.
            Feature availability may depend on your selected paid plan after activation.
          </p>
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/subscription">Upgrade now</Link>
            <Link to="/settings/subscription">View Plans</Link>
          </div>
        </section>
      );

    case 'TrialNotStarted':
      return (
        <section className="trial-card trial-card--pending" aria-label="Free trial status" data-testid="trial-status-not-started">
          <h3 className="trial-card__title">Trial Not Started</h3>
          <p>
            <strong>{subscription.planName}</strong> is selected. Start your {subscription.trialLengthDays}-day free trial whenever
            you are ready — the clock has not begun, and no credit card is needed.
          </p>
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/subscription">Start free trial</Link>
            <Link to="/settings/subscription">View Plans</Link>
          </div>
        </section>
      );

    case 'TrialExpired':
      return (
        <section className="trial-card trial-card--attention" aria-label="Free trial status" data-testid="trial-status-expired">
          <h3 className="trial-card__title">Your Trial Has Ended</h3>
          <p>Activate a subscription to continue using paid Escochex features. Your setup data remains saved.</p>
          <p className="muted">The company is read-only until you subscribe.</p>
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/subscription">Subscribe</Link>
            <Link to="/settings/subscription">View Plans</Link>
          </div>
        </section>
      );

    case 'Active':
    case 'PendingChange':
      return (
        <section className="trial-card" aria-label="Subscription status" data-testid="trial-status-paid">
          <h3 className="trial-card__title">Current plan: {subscription.planName}</h3>
          <p>Your subscription is active.</p>
          {subscription.cancelAtPeriodEnd && (
            <p className="muted">It is set to cancel on {formatUtcDate(subscription.currentPeriodEndUtc)}; you keep full access until then.</p>
          )}
          {subscription.pendingPlanCode && (
            <p className="muted">A plan change is scheduled for {formatUtcDate(subscription.pendingPlanEffectiveUtc)}, the end of the current billing period.</p>
          )}
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/subscription">Manage Subscription</Link>
            <Link to="/settings/billing">Billing</Link>
          </div>
        </section>
      );

    case 'PastDueGrace':
      return (
        <section className="trial-card trial-card--pending" aria-label="Subscription status" data-testid="trial-status-past-due-grace">
          <h3 className="trial-card__title">Payment failed — update your payment method</h3>
          <p>
            We couldn&apos;t collect your last payment.
            {subscription.graceEndsUtc ? ` Your plan access continues until ${formatUtcDate(subscription.graceEndsUtc)}.` : ' Your plan access continues for now.'}
          </p>
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/billing">Update payment method</Link>
          </div>
        </section>
      );

    case 'PastDueRestricted':
      return (
        <section className="trial-card trial-card--attention" aria-label="Subscription status" data-testid="trial-status-past-due-restricted">
          <h3 className="trial-card__title">Payment failed — the company is read-only</h3>
          <p>Update your payment method to restore full access. Your records are safe, and billing stays available.</p>
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/billing">Update payment method</Link>
          </div>
        </section>
      );

    case 'Canceled':
      return (
        <section className="trial-card trial-card--attention" aria-label="Subscription status" data-testid="trial-status-canceled">
          <h3 className="trial-card__title">Subscription canceled</h3>
          <p>The company is read-only. Reactivate your subscription to make changes again. Your records are kept.</p>
          <div className="trial-card__actions">
            <Link className="trial-card__cta" to="/settings/billing">Reactivate subscription</Link>
          </div>
        </section>
      );

    default:
      return (
        <section className="trial-card trial-card--attention" aria-label="Subscription status" data-testid="trial-status-other">
          <h3 className="trial-card__title">Subscription Required</h3>
          <p>Your subscription is not currently active. Review your plan to restore access to paid features.</p>
          <div className="trial-card__actions"><Link className="trial-card__cta" to="/settings/subscription">View Plans</Link></div>
        </section>
      );
  }
}
